import { reconcilePermissionPrerequisites } from '@utazzo/shared';
import { toast } from 'sonner';

/**
 * Page-access prerequisites for the two /roles matrices (role grants and
 * per-user extras).
 *
 * Some codes only work alongside another: a Marketing report section needs
 * "Marketing reports (page access)", a Settings tab needs "Settings (page
 * access)". Admins should not have to know that, so a change is reconciled
 * here - ticking a section also ticks its page access, unticking the page
 * access also unticks its sections - and a toast says what else moved, so
 * nothing changes behind their back. Nothing is saved until they press Save.
 */

interface ModuleLabel {
  moduleKey: string;
  label: string;
}

/** `ops.marketing_reports.read` -> "Marketing reports (page access) · read". */
export const describePermissionCode = (
  code: string,
  modules: ReadonlyArray<ModuleLabel>,
): string => {
  const dot = code.lastIndexOf('.');
  const moduleKey = dot > 0 ? code.slice(0, dot) : code;
  const action = dot > 0 ? code.slice(dot + 1) : '';
  const label = modules.find((m) => m.moduleKey === moduleKey)?.label ?? moduleKey;
  return action ? `${label} · ${action}` : label;
};

/**
 * Apply one matrix change with its prerequisites kept consistent, and tell the
 * admin about anything that moved besides what they clicked.
 */
export const applyWithPrerequisites = (
  prev: ReadonlySet<string>,
  next: ReadonlySet<string>,
  alsoHeld: ReadonlySet<string>,
  modules: ReadonlyArray<ModuleLabel>,
): Set<string> => {
  const result = reconcilePermissionPrerequisites(prev, next, alsoHeld);
  const names = (codes: string[]): string =>
    codes.map((code) => describePermissionCode(code, modules)).join(', ');
  if (result.added.length > 0) {
    toast.info(`Also ticked: ${names(result.added)}`, {
      description:
        'This opens the page - without it the section or tab you picked would not show at all. Press Save to apply.',
    });
  }
  if (result.removed.length > 0) {
    toast.info(`Also unticked: ${names(result.removed)}`, {
      description: 'These do nothing without the page access you removed. Press Save to apply.',
    });
  }
  return result.next;
};
